// ─── API configuration ───────────────────────────────────────
// The Azure Function endpoint that proxies to HRD365 /api/SupportPlugin/ticket/createwithattachment
export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://salescopilotskeusuat.azurewebsites.net/api";

// Scope required to call the backend Azure Function (same scope used for CPP auth)
export const API_SCOPE =
  process.env.REACT_APP_API_SCOPE ||
  "api://5d437e13-722e-4a8d-8f4f-3158cf570e94/user_impersonation";

// ─── Topic → Subtopic mapping ─────────────────────────────────
export const TOPIC_SUBTOPIC_MAP: Record<string, string[]> = {
  "Data Request": ["Seller"],
  "Employee Data Management": ["Taxonomy", "Incentive Target Amount", "Transition Allowance Support"],
  "Incentive Program": ["Terms and Conditions", "Eligibility", "Payment Calculations"],
  "MSX Earnings": ["Manager Reporting", "Seller Reporting", "Refresh Dates", "Access Issues"],
  "Payment": ["Not Received", "Recovery", "Dispute"],
  "Plan": ["Participant Plan Agreement", "Plan Understanding"],
  "Quota or Target": ["Zero Quota or Target", "Quota Management", "Quota Acknowledgement", "Unexpected Quota Change"],
  "Revenue": ["Manual Revenue Adjustment", "Multipliers or Modifiers", "Missing or Negative"],
  "Territory Management": ["Account Alignment"],
};

export const TOPIC_OPTIONS = Object.keys(TOPIC_SUBTOPIC_MAP);

// ─── Topic+Subtopic → Workspace resolution ────────────────────
// Mirrors backend EarningsWizardHelper.ResolveEarningsWorkspace / EarningsTopicSubtopicMap.json
// Workspace is a JSON-encoded string with IncidentConfiguration (AskHR routing queue) and TargetSystem.
export const WORKSPACE_VALUES = {
  "Earnings - Payouts": '{"IncidentConfiguration":"msx-earnings-payouts","TargetSystem":"HRD365"}',
  "Earnings - Quota":   '{"IncidentConfiguration":"msx-earnings-quota","TargetSystem":"HRD365"}',
  "Earnings - Revenue": '{"IncidentConfiguration":"msx-earnings-revenue","TargetSystem":"HRD365"}',
} as const;

export type WorkspaceDisplayName = keyof typeof WORKSPACE_VALUES;

// Maps each Topic → Subtopic to a workspace display name, which then resolves to the JSON value above.
export const SUBTOPIC_WORKSPACE_MAP: Record<string, Record<string, WorkspaceDisplayName>> = {
  "Data Request": {
    "Seller": "Earnings - Quota",
  },
  "Employee Data Management": {
    "Taxonomy": "Earnings - Quota",
    "Incentive Target Amount": "Earnings - Quota",
    "Transition Allowance Support": "Earnings - Quota",
  },
  "Incentive Program": {
    "Terms and Conditions": "Earnings - Quota",
    "Eligibility": "Earnings - Quota",
    "Payment Calculations": "Earnings - Payouts",
  },
  "MSX Earnings": {
    "Manager Reporting": "Earnings - Quota",
    "Seller Reporting": "Earnings - Quota",
    "Refresh Dates": "Earnings - Quota",
    "Access Issues": "Earnings - Quota",
  },
  "Payment": {
    "Not Received": "Earnings - Payouts",
    "Recovery": "Earnings - Payouts",
    "Dispute": "Earnings - Payouts",
  },
  "Plan": {
    "Participant Plan Agreement": "Earnings - Quota",
    "Plan Understanding": "Earnings - Quota",
  },
  "Quota or Target": {
    "Zero Quota or Target": "Earnings - Quota",
    "Quota Management": "Earnings - Quota",
    "Quota Acknowledgement": "Earnings - Quota",
    "Unexpected Quota Change": "Earnings - Quota",
  },
  "Revenue": {
    "Manual Revenue Adjustment": "Earnings - Revenue",
    "Multipliers or Modifiers": "Earnings - Revenue",
    "Missing or Negative": "Earnings - Revenue",
  },
  "Territory Management": {
    "Account Alignment": "Earnings - Quota",
  },
};

// ─── File attachment constraints (per HRD365 API v2 spec §2.2 / §5) ──
// Allowed extensions: 11 types per v2 spec (v2 removed .html, .htm for XSS risk)
export const ALLOWED_EXTENSIONS = new Set([
  "txt","md","pdf","json","docx","pptx","png","jpg","jpeg","gif","bmp",
]);

export const ACCEPT_STRING = Array.from(ALLOWED_EXTENSIONS).map((e) => `.${e}`).join(",");

/** Max files per request — API returns 400 if exceeded (§5) */
export const MAX_FILES = 5;

/** Max file size in bytes — 10 MB per file; larger files are skipped server-side (§5) */
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB
